import React from 'react';
import { formatActivityValue, formatDateForDisplay } from '../utils/formatters';

const TableRow = ({ 
  entry, 
  isEditing, 
  onEdit, 
  onDelete, 
  onUpdate, 
  onCancelEdit,
  formData,
  handleChange,
  isSaving 
}) => {
  if (!entry || !entry.type) return null;

  const date = new Date(entry.datetime);
  const formattedTime = date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
  const datetime = `${formatDateForDisplay(date)} ${formattedTime}`;
  const typeName = entry.type.charAt(0).toUpperCase() + entry.type.slice(1);

  // Determine input type and step based on activity type
  const getInputProps = () => {
    const currentActivityType = isEditing ? (formData.activityType || entry.type) : entry.type;
    switch(currentActivityType) {
      case 'sleep':
        return { type: 'number', step: '0.5' };
      case 'weight':
        return { type: 'number', step: '0.1' };
      case 'glucose':
        return { type: 'number', step: '0.1' };
      default:
        return { type: 'text' };
    }
  };

  const inputProps = getInputProps();
  const currentActivityType = isEditing ? (formData.activityType || entry.type) : entry.type;

  // Special handler for activity type changes
  const handleActivityTypeChange = (e) => {
    const newActivityType = e.target.value;
    
    // Create a special event to clear the old value when activity type changes
    if (newActivityType !== currentActivityType) {
      // Clear the old activity value
      const clearOldValueEvent = {
        target: { name: currentActivityType, value: '' }
      };
      handleChange(clearOldValueEvent);
    }
    
    // Set the new activity type
    handleChange(e);
  };

  return (
    <tr className="border-b hover:bg-gray-50">
      <td className="px-4 py-2 border border-gray-200 text-sm date-column">
        {isEditing ? (
          <div className="space-y-1">
            <input
              type="date"
              value={formData.date || ''}
              onChange={handleChange}
              name="date"
              className="w-full p-1 border rounded text-xs"
            />
            <input
              type="time"
              value={formData.time || ''}
              onChange={handleChange}
              name="time"
              className="w-full p-1 border rounded text-xs"
            />
          </div>
        ) : (
          <div className="lg:inline">
            <span className="block lg:inline">{formatDateForDisplay(date)}</span>
            <span className="block lg:inline lg:ml-1">{formattedTime}</span>
          </div>
        )}
      </td>
      <td className="px-4 py-2 border border-gray-200 text-sm type-column">
        {isEditing ? (
          <select
            value={formData.activityType || entry.type}
            onChange={handleActivityTypeChange}
            name="activityType"
            className="w-full p-1 border rounded text-xs"
          >
            <option value="glucose">Glucose</option>
            <option value="bloodPressure">Blood Pressure</option>
            <option value="medication">Medication</option>
            <option value="exercise">Exercise</option>
            <option value="sleep">Sleep</option>
            <option value="meal">Meal</option>
            <option value="weight">Weight</option>
          </select>
        ) : (
          typeName
        )}
      </td>
      <td className="px-4 py-2 border border-gray-200 text-sm value-column">
        {isEditing ? (
          <div className="space-y-1">
            <input
              type={inputProps.type}
              step={inputProps.step}
              value={formData[currentActivityType] || ''}
              onChange={(e) => handleChange(e)}
              name={currentActivityType}
              className="w-full p-1 border rounded text-xs"
              placeholder={`Enter ${currentActivityType}`}
            />
            {currentActivityType === 'glucose' && (
              <select
                value={formData.glucoseUnit || 'mmol'}
                onChange={handleChange}
                name="glucoseUnit"
                className="w-full p-1 border rounded text-xs"
              >
                <option value="mmol">mmol/L</option>
                <option value="mg">mg/dL</option>
              </select>
            )}
          </div>
        ) : (
          formatActivityValue(entry.type, entry.value, entry.unit)
        )}
      </td>
      <td className="px-4 py-2 border border-gray-200 text-sm notes-column">
        {isEditing ? (
          <input
            type="text"
            value={formData.notes || ''}
            onChange={handleChange}
            name="notes"
            className="w-full p-1 border rounded text-xs"
          />
        ) : entry.notes}
      </td>
      <td className="px-4 py-2 border border-gray-200 text-sm actions-column">
        {isEditing ? (
          <div className="space-x-2">
            <button
              onClick={() => onUpdate(entry.id, entry.type)}
              className="text-green-600 hover:text-green-800 disabled:opacity-50"
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={onCancelEdit}
              className="text-gray-600 hover:text-gray-800"
              disabled={isSaving}
            >
              Cancel
            </button>
          </div>
        ) : (
          <>
            <button
              onClick={() => onEdit(entry, entry.type)}
              className="text-cyan-700 hover:text-cyan-500 mr-2"
            >
              Edit
            </button>
            <button
              onClick={() => onDelete(entry.id, entry.type)}
              className="text-rose-500 hover:text-rose-300"
            >
              Delete
            </button>
          </>
        )}
      </td>
    </tr>
  );
};

export default TableRow;
